import {
    Component,
    computed,
    DestroyRef,
    effect,
    inject,
    input,
    Signal,
    signal,
} from '@angular/core';
import { MatError, MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { CustomTranslatePipe } from '../../shared/pipes/custom-translate.pipe';
import {
    AbstractControl,
    FormControl,
    ReactiveFormsModule,
} from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { DefaultTranslatePipe } from '../../shared/pipes/default-translate.pipe';
import { ErrorsTranslatePipe } from '../../shared/pipes/errors-translate.pipe';
import { MatInputModule } from '@angular/material/input';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { CodeTableService } from '../../core/code-table.service';
import { CodeLabelEntry } from '../../shared/interfaces/code-label-entry.interface';
import { ConfigurationHandlerService } from '../../core/configuration-handler.service';
import {
    MatDatepicker,
    MatDatepickerModule,
    MatDatepickerToggle,
} from '@angular/material/datepicker';
import { SystemDateFormatService } from '@exl-ng/mulo-core';
import { OtbAccessPolicyType } from './otb-access-policy-type';

@Component({
    selector: 'esp-access-rights',
    imports: [
        MatIcon,
        MatTooltip,
        CustomTranslatePipe,
        ReactiveFormsModule,
        MatSelectModule,
        DefaultTranslatePipe,
        ErrorsTranslatePipe,
        MatInputModule,
        MatDatepickerModule,
    ],
    templateUrl: './access-rights.component.html',
    styleUrl: './access-rights.component.scss',
})
export class AccessRightsComponent {
    private ctSvc = inject(CodeTableService);
    private cfgSvc = inject(ConfigurationHandlerService);
    private destroyRef = inject(DestroyRef);
    private sysDateFormatSvc = inject(SystemDateFormatService);

    readonly autoPopulated = input(false);

    readonly policyFieldControl = input<AbstractControl>(new FormControl());
    readonly policyFieldOptions: Signal<CodeLabelEntry[]>;
    readonly policyFieldLabel = input('Access rights policy');
    readonly policyFieldHint = input<string>(undefined);

    readonly isEmbargo = signal(false);
    readonly embargoDateControl = input<AbstractControl>(new FormControl());
    readonly embargoDateLabel = input('End date');
    readonly embargoDateHint = input<string>(
        this.sysDateFormatSvc.javaDateFormat,
    );
    readonly embargoPeriodEnabled = input(true);
    readonly embargoPeriodControl = input<AbstractControl>(new FormControl());
    readonly embargoPeriodOptions: Signal<CodeLabelEntry[]>;
    readonly embargoPeriodLabel = input('Select period from publication date');
    readonly embargoPeriodHint = input<string>(undefined);

    readonly localPolicyEnabled = input(true);
    readonly isLocalPolicy = signal(false);
    readonly localPolicyControl = input<AbstractControl>(new FormControl());
    readonly localPolicyOptions = computed(
        () => this.cfgSvc.config()?.localAccessRights,
    );
    readonly localPolicyLabel = input('Locally defined policy');
    readonly localPolicyHint = input<string>(undefined);

    readonly allowsNote = signal(false);
    readonly noteFieldControl = input<AbstractControl>(new FormControl());
    readonly noteFieldLabel = input('Reason');
    readonly noteFieldHint = input<string>(undefined);

    constructor() {
        effect(() => {
            this.policyFieldControl()
                ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe((value) => {
                    this.isEmbargo.set(value === OtbAccessPolicyType.Embargo);
                    this.isLocalPolicy.set(value === OtbAccessPolicyType.Local);

                    // see AccessRightsDataHandlerForAssetBean.setAccessRightPolicyNoteVisibility
                    this.allowsNote.set(
                        !!value &&
                            ![
                                OtbAccessPolicyType.Open,
                                OtbAccessPolicyType.Embargo,
                            ].includes(value),
                    );
                });
        });

        this.policyFieldOptions = toSignal(
            this.ctSvc.getCodeTable('AssetAndFileAccessRightsLabels', true),
        );
        this.embargoPeriodOptions = toSignal(
            this.ctSvc.getCodeTable(
                'EsploroAccessRightsPolicyPredefinedTypes',
                true,
            ),
        );
    }

    protected readonly otbAccessPolicyType = OtbAccessPolicyType;
}
